import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Fab, InputBase, List, ListItem, ListItemIcon, ListItemText, ListSubheader, Paper, TextField } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { Delete, Person } from '@material-ui/icons'
import firebase from 'firebase/app'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useHistory } from 'react-router'

import { Collections } from 'Collections'
import { useAuthUser } from 'components/AuthUserProvider'
import { FullscreenLoading } from 'components/FullscreenLoading'
import { useUnselectOrg } from 'components/OrgProvider'
import { PageNavigation } from 'components/PageNavigation'
import { ProfileSaveButton } from 'components/ProfileSaveButton'
import { auth } from 'FirebaseConfigs'
import { useSyncedState } from 'hooks/useSyncedState'
import { AnalyticsEvent } from 'models/AnalyticsEvent'
import { nameFrom } from 'models/Name'
import { OrgUser } from 'models/OrgUser'
import { Paths } from 'Paths'
import { splitName } from 'screens/Users/CreateAppUserScreen'
import { dateFormat } from 'Util/date.utils'

import { useAppUser } from './components/AppUserProvider'
import { ImageButton } from './components/ImageButton'
import { useOrgUser } from './components/OrgUserProvider'
import { FirebaseUtils } from './firebaseUtils/FirebaseUtils'
import { AppRoles } from './models/AppRoles'
import { AppUser } from './models/AppUser'
import { avatarImageFrom } from './models/AvatarImage'
import { emailFrom } from './models/Email'
import { phoneFrom } from './models/Phone'
import { NameInput } from './screens/MyOrgUserScreen'
import { WarningModal } from './screens/Users/OrgUserScreen'

type AppUserRolePickerProps = {
    appUser: AppUser
    ownAppUser: AppUser
    isOwnUser: boolean
}

export function AppUserRolePicker({ appUser, isOwnUser, ownAppUser }: AppUserRolePickerProps) {
    const [ownerRoleWarning, setOwnerRoleWarning] = useState('')
    const { enqueueSnackbar } = useSnackbar()

    if (!ownAppUser.appRoles?.map[AppRoles.FlatEnum.ADMIN]) {
        return <></>
    }

    return (
        <>
            {ownerRoleWarning ? (
                <WarningModal title="Kan ikke fjerne rolle" onClose={() => setOwnerRoleWarning('')}>
                    <Typography variant="subtitle1">{ownerRoleWarning}</Typography>
                </WarningModal>
            ) : null}
            <ListSubheader>Roller</ListSubheader>
            {(
                AppRoles.FlatEnumList.map((role) => {
                    const isSelected = !!appUser?.appRoles?.map[role];
                    const isAdminRole = role === AppRoles.FlatEnum.ADMIN
                    //Is this role OWNER and are you trying to change it for your own user?
                    const isOwnAdminRole = (isOwnUser && isAdminRole)
                    const canSelect = !!ownAppUser?.appRoles?.map[AppRoles.FlatEnum.ADMIN]
                    const newValue = !isSelected || firebase.firestore.FieldValue.delete()
                    function toggleSelected() {
                        if (isOwnAdminRole) {
                            setOwnerRoleWarning('Hvis du ikke lenger ønsker å være Admin, få en annen Admin til å fjerne statusen for deg')
                        } else if (canSelect && !isOwnAdminRole) {
                            if (!ownAppUser) {
                                console.error('No ownAppUser state!')
                                return
                            }
                            FirebaseUtils.updateDocument<AppUser>(
                                false,
                                Collections.APP_USERS,
                                appUser.id,
                                appUser,
                                null,
                                isSelected ? AnalyticsEvent.ADDED_APP_ROLE : AnalyticsEvent.REMOVED_APP_ROLE,
                                isOwnUser ? AnalyticsEvent.ADDED_OWN_APP_ROLE : AnalyticsEvent.REMOVED_OWN_APP_ROLE,
                                AnalyticsEvent.UPDATE_APP_ROLE_ERROR,
                                enqueueSnackbar,
                                'Vellykket!',
                                {
                                    appRoles: {
                                        map: {
                                            //TODO: typing is not working here
                                            [role]: newValue,
                                        }
                                    }
                                },
                                () => ({
                                    isOwnUser,
                                    ownAppUserId: appUser.id,
                                    role,
                                }),
                            )
                        }
                    }

                    const checkId = "check-" + role.toLowerCase().replaceAll('_', '-')

                    return (
                        <ListItem
                            key={role}
                            //@ts-ignore
                            button={canSelect ? true : false}
                            onClick={canSelect ? toggleSelected : undefined}
                            divider
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={isSelected}
                                    edge="start"
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={!canSelect || isOwnAdminRole}
                                    inputProps={{ 'aria-labelledby': checkId }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id={checkId}
                                primary={AppRoles.Flat[role].name}
                                secondary={AppRoles.Flat[role].description}
                            />
                        </ListItem>
                    )
                })
            )}
        </>
    )
}

type AddCredentialModalProps = {
    onClose: (user?: firebase.User | null) => void
    title: string
    onCancel: () => void
    email: string
}

enum AuthProvider {
    GOOGLE,
    EMAIL,
}

function AddCredentialModal({ onClose, onCancel, title, email }: AddCredentialModalProps) {
    const { enqueueSnackbar } = useSnackbar()
    const [authProvider, setAuthProvider] = useState<AuthProvider | null>(null)
    const [emailAddress, setEmailAddress] = useState<string>(email || '')
    const [password, setPassword] = useState<string>('')

    const [loading, setLoading] = useState(false)

    //TODO: Add validation
    const isValid = !!emailAddress && !!password

    function connect(promise: Promise<firebase.auth.UserCredential>) {
        setLoading(true)
        promise.then(async credential => {
            enqueueSnackbar('Vellykket!', { variant: 'success' })
            onClose(credential.user)
        }).catch((error) => {
            console.error(error)
            enqueueSnackbar(error.message, { variant: 'error' })
        }).finally(() => {
            setLoading(false)
        })
    }

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <List>
                    {!authProvider ? (
                        <ListItem>
                            <Button variant="contained" color="primary" fullWidth onClick={() => {
                                if (auth.currentUser) {
                                    connect(auth.currentUser.linkWithPopup(new firebase.auth.GoogleAuthProvider()))
                                }
                            }}>Google</Button>
                        </ListItem>
                    ) : null}
                    {!authProvider ? (
                        <ListItem>
                            <Button variant="contained" color="primary" fullWidth onClick={() => {
                                setAuthProvider(AuthProvider.EMAIL)
                            }}>E-post</Button>
                        </ListItem>
                    ) : null}
                    {authProvider === AuthProvider.EMAIL ? (
                        <>
                            <ListItem>
                                <TextField
                                    type="email"
                                    variant="outlined"
                                    value={emailAddress}
                                    onChange={e => setEmailAddress(e.target.value)}
                                    label="E-post"
                                    autoFocus
                                    fullWidth
                                />
                            </ListItem>
                            <ListItem>
                                <TextField
                                    type="password"
                                    variant="outlined"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    label="Passord"
                                    fullWidth
                                />
                            </ListItem>
                        </>
                    ) : null}
                </List>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} onClick={onCancel}>Avbryt</Button>
                {authProvider === AuthProvider.EMAIL ? (
                    <Button
                        disabled={!isValid || loading}
                        onClick={() => {
                            if (auth.currentUser) {
                                connect(auth.currentUser.linkWithCredential(firebase.auth.EmailAuthProvider.credential(emailAddress, password)))
                            }
                        }}
                        color="primary"
                        autoFocus
                    >{'Ok'}</Button>
                ) : null}
            </DialogActions>
        </Dialog>
    )
}

export function MyAppUserScreen() {
    const history = useHistory()
    const [authUser, loadingAuthUser] = useAuthUser()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showAuthProviderModal, setShowAuthProviderModal] = useState(false)
    const [showDeleteUser, setShowDeleteUser] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const [myOrgUsers, loadingMyOrgUsers] = FirebaseUtils.useCollectionData<OrgUser>(
        true,
        Collections.ORG_USERS,
        AnalyticsEvent.GET_ORGS_ERROR,
        {},
        (query, path) => appUser && query.where(path.data.userId, '==', appUser.data.id),
    )

    const hasOrgUsers = !!myOrgUsers?.length

    const [name, setName] = useSyncedState(appUser?.data.contactInfo?.name || null)
    const [phoneNumber, setPhoneNumber] = useSyncedState(appUser?.data.contactInfo?.phone?.phoneNumber || '')
    const [personalUserEmail, setPersonalUserEmail] = useSyncedState(appUser?.data.contactInfo?.email?.email || '')
    const [avatarImageUrl, setAvatarImageUrl] = useSyncedState(appUser?.data.avatarImage?.original.url || null)

    const [unselectOrg] = useUnselectOrg()

    const loading = loadingAppUser || loadingOrgUser || loadingAuthUser || loadingMyOrgUsers

    const hasChanges = name?.fullName !== appUser?.data.contactInfo?.name?.fullName
        || phoneNumber !== (appUser?.data.contactInfo?.phone?.phoneNumber || '')
        || personalUserEmail !== (appUser?.data.contactInfo?.email?.email || '')
        || avatarImageUrl !== (appUser?.data.avatarImage?.original.url || null)

    //TODO: Validation
    const isValid = hasChanges

    function save() {
        if (!appUser) {
            console.error('No appUser state!')
            return
        }
        if (isValid) {
            FirebaseUtils.updateDocument<AppUser>(
                false,
                Collections.APP_USERS,
                appUser.data.id,
                appUser.data,
                null,
                AnalyticsEvent.UPDATED_APP_USER,
                AnalyticsEvent.UPDATED_OWN_APP_USER,
                AnalyticsEvent.UPDATE_APP_USER_ERROR,
                enqueueSnackbar,
                'Vellykket!',
                {
                    contactInfo: {
                        name,
                        phone: phoneFrom(phoneNumber),
                        email: emailFrom(personalUserEmail),
                    },
                    avatarImage: avatarImageFrom(avatarImageUrl),
                },
            )
        }
    }

    const connectAuthProviderButton = authUser?.isAnonymous ? (
        <Button
            style={{ marginBottom: 40, marginTop: 20 }}
            variant="contained"
            color="primary"
            onClick={() => {
                setShowAuthProviderModal(true)
            }}
        >Legg til innlogging</Button>
    ) : null

    const content = loading ? <FullscreenLoading screenId="My App User" /> : (
        (!appUser) ? (
            <List>
                <ListItem>
                    <ListItemText primary="Kunne ikke finne brukeren" />
                </ListItem>
            </List>
        ) : (
            <List>
                <ListItem>
                    <Box mx="auto" mt="40px">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Paper
                                elevation={3}
                                style={{
                                    position: 'relative',
                                    //width: 300,
                                    //height: 300,
                                    borderRadius: 18,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Fab
                                    size="medium"
                                    style={{ position: 'absolute', bottom: 40, right: -16 }}
                                    onClick={() => setShowDeleteUser(true)}
                                    color="secondary"
                                    aria-label="delete"
                                >
                                    <Delete />
                                </Fab>
                                <ImageButton
                                    label="Profilbilde"
                                    value={avatarImageUrl}
                                    onChange={setAvatarImageUrl}
                                >
                                    <Person />
                                </ImageButton>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        marginTop: 18,
                                        marginBottom: 18,
                                    }}
                                >
                                    <NameInput
                                        value={name}
                                        onChange={setName}
                                    />
                                    <InputBase
                                        value={phoneNumber}
                                        placeholder="Personlig tlf"
                                        onChange={e => setPhoneNumber(e.target.value)}
                                        type="number"
                                        inputProps={{
                                            'aria-label': 'phone number input',
                                            style: {
                                                fontSize: 16,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                    <InputBase
                                        value={personalUserEmail}
                                        placeholder="Personlig E-post"
                                        onChange={e => setPersonalUserEmail(e.target.value)}
                                        inputProps={{
                                            'aria-label': 'personal email input',
                                            style: {
                                                fontSize: 16,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                    {connectAuthProviderButton}
                                    {showAuthProviderModal ? (
                                        <AddCredentialModal
                                            title="Legg til innloggingsmetode"
                                            email={personalUserEmail}
                                            onCancel={() => setShowAuthProviderModal(false)}
                                            onClose={(user) => {
                                                setShowAuthProviderModal(false)
                                                const providerData = user?.providerData[0]
                                                if (providerData?.email) {
                                                    setPersonalUserEmail(providerData.email)
                                                } else if (personalUserEmail) {
                                                    user?.updateEmail(personalUserEmail)
                                                }
                                                if (providerData?.displayName) {
                                                    const names = splitName(providerData.displayName)
                                                    if (names?.firstName) {
                                                        setName(nameFrom(names.firstName, names?.lastName))
                                                    }
                                                } else if (name?.fullName) {
                                                    authUser?.updateProfile({
                                                        displayName: name.fullName,
                                                    })
                                                }
                                                if (providerData?.photoURL) {
                                                    setAvatarImageUrl(providerData.photoURL)
                                                } else if (avatarImageUrl) {
                                                    authUser?.updateProfile({
                                                        photoURL: avatarImageUrl,
                                                    })
                                                }
                                                if (providerData?.phoneNumber) {
                                                    setPhoneNumber(providerData.phoneNumber)
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <Typography variant="subtitle2">Opprettet: {dateFormat.format(appUser.metadata.createdAt.toDate())}</Typography>
                                </div>
                                <ProfileSaveButton
                                    hasChanges={hasChanges}
                                    save={save}
                                    isValid={isValid}
                                />
                            </Paper>
                        </div>
                    </Box>
                </ListItem>
                {(!loading) ? <AppUserRolePicker appUser={appUser.data} ownAppUser={appUser.data} isOwnUser /> : null}
            </List>
        )
    )


    return (
        <PageNavigation
            title="Min App-Bruker"
            hasUnsavedChanges={hasChanges}
        >
            {showDeleteUser ? (
                <Dialog
                    open
                    onClose={() => {
                        setShowDeleteUser(false)
                        setShowConfirmation(false)
                    }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Fjern bruker"}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" display="inline">
                            {
                                hasOrgUsers ? (
                                    'Du må forlate alle organisasjoner før du sleter brukeren din'
                                ) : (
                                    showConfirmation
                                        ? 'Er du sikker? Dette kan ikke angres!'
                                        : 'Ønsker du å fjerne brukeren fra appen? Dette kan ikke angres'
                                )
                            }
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        {(hasOrgUsers ? (
                            <Button color="primary" onClick={() => unselectOrg(AnalyticsEvent.PREPARING_TO_DELETE_APP_USER)}>Gå til mine organisjoner</Button>
                        ) : (showConfirmation ? (
                            <Button
                                color="secondary"
                                disabled={loading}
                                onClick={() => {
                                    if (appUser?.data.id) {
                                        FirebaseUtils.deleteDocument(
                                            true,
                                            Collections.APP_USERS,
                                            appUser.data.id,
                                            appUser.data,
                                            orgUser?.data || null,
                                            AnalyticsEvent.DELETED_APP_USER,
                                            AnalyticsEvent.DELETED_OWN_APP_USER,
                                            AnalyticsEvent.DELETED_APP_USER,
                                            enqueueSnackbar,
                                            'Bruker slettet',
                                            () => ({ appUserId: appUser?.data.id, }),
                                            async () => {
                                                if (auth.currentUser) {
                                                    await auth.currentUser.delete()
                                                    enqueueSnackbar('Bruker slettet', { variant: 'success' })
                                                } else {
                                                    enqueueSnackbar('Bruker kunne ikke slettes', { variant: 'error' })
                                                    console.error('Unable to delete user from auth because auth.currentUser is null')
                                                }
                                            },
                                        )
                                        history.push(Paths.DASHBOARD)
                                    }
                                }}
                            >Ja</Button>
                        ) : (
                            <Button
                                color="secondary"
                                onClick={() => setShowConfirmation(true)}
                                disabled={loading}
                            >
                                Slett brukeren min
                            </Button>
                        )
                        ))}
                        <Button
                            color="primary"
                            onClick={() => {
                                setShowDeleteUser(false)
                                setShowConfirmation(false)
                            }}
                        >Avbryt</Button>
                    </DialogActions>
                </Dialog>
            ) : null
            }
            {content}
        </PageNavigation >
    )
}
